import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@/components/ui/tooltip";
import LoadingIndicator from "@/components/ui/loading-indicator";
import {
  outlineIconButton,
  segmentedTabIdle,
  segmentedTabSelected,
  segmentedTabTrack,
} from "@/lib/status-styles";
import { cn } from "@/lib/utils";

/**
 * Operating Points Tabs Component
 * @param {Object} props
 * @param {Array<{id: string, label: string}>} props.tabs - Array of tab objects
 * @param {string} props.activeTab - Currently active tab ID
 * @param {Function} props.onTabChange - Callback when tab is changed
 * @param {boolean} props.canDeleteTab - Whether the active tab can be deleted
 * @param {Function} props.onCreateView - Callback to create a new view
 * @param {Function} props.onDeleteView - Callback to delete the active view
 * @param {boolean} props.isLoadingTags - When true, toolbar actions are disabled (e.g. while universal descriptors load)
 * @param {Function} props.checkTabHasUnsavedChanges - Function to check if a tab has unsaved changes
 */
export default function OperatingPointsTabs({
  tabs,
  activeTab,
  onTabChange,
  canDeleteTab,
  onCreateView,
  onDeleteView,
  isLoadingTags = false,
  checkTabHasUnsavedChanges,
}) {
  return (
    <div className='shrink-0 flex items-center justify-between px-6 py-4 border-b border-border bg-card rounded-t-lg gap-4'>
      <nav
        className={cn(
          segmentedTabTrack,
          "max-w-full min-w-0 flex-1 overflow-x-auto"
        )}>
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => onTabChange(tab.id)}
              className={cn(
                "cursor-pointer whitespace-nowrap px-3 py-1.5 text-sm font-medium transition-colors",
                isActive ? segmentedTabSelected : segmentedTabIdle
              )}>
              {tab.label}
              {checkTabHasUnsavedChanges?.(tab.id) && (
                <span className='ml-1'>*</span>
              )}
            </button>
          );
        })}
      </nav>
      <div className='flex items-center gap-2'>
        <Tooltip>
          <TooltipTrigger asChild>
            <div>
              <Button
                variant='outline'
                size='sm'
                onClick={onCreateView}
                disabled={isLoadingTags}
                className={cn(
                  outlineIconButton,
                  "h-8 w-8 p-0 disabled:opacity-50 disabled:cursor-not-allowed"
                )}>
                {isLoadingTags ? (
                  <LoadingIndicator size='sm' />
                ) : (
                  <Plus className='h-4 w-4' />
                )}
              </Button>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p>{isLoadingTags ? "Loading..." : "Add view"}</p>
          </TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <div>
              <Button
                variant='outline'
                size='sm'
                onClick={onDeleteView}
                disabled={!canDeleteTab || isLoadingTags}
                className={cn(
                  outlineIconButton,
                  "h-8 w-8 p-0 disabled:opacity-50 disabled:cursor-not-allowed"
                )}>
                {isLoadingTags ? (
                  <LoadingIndicator size='sm' />
                ) : (
                  <Trash2 className='h-4 w-4' />
                )}
              </Button>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p>
              {isLoadingTags
                ? "Loading..."
                : canDeleteTab
                  ? "Delete view"
                  : "Default views cannot be deleted"}
            </p>
          </TooltipContent>
        </Tooltip>
      </div>
    </div>
  );
}
